// Wraps protected /app/* routes. Checks Supabase session + admin app_role
// from JWT claims; redirects to /login if not authenticated or not admin.
//
// Renders <SidebarLayout> with nested <Outlet /> for the actual page.

import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { supabase } from "@/lib/supabase";
import { parseJwt } from "@/lib/jwt";
import { SidebarLayout } from "@/components/SidebarLayout";

type AuthState = "checking" | "authed" | "unauth";

export function ProtectedLayout() {
  const [state, setState] = useState<AuthState>("checking");
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        if (!cancelled) setState("unauth");
        return;
      }
      const payload = parseJwt<{ app_role?: string }>(token);
      if (payload.app_role !== "admin") {
        // Non-admin session on admin console — sign out + redirect.
        await supabase.auth.signOut();
        if (!cancelled) setState("unauth");
        return;
      }
      if (!cancelled) setState("authed");
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (!session?.access_token) setState("unauth");
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state === "checking") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state === "unauth") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return (
    <SidebarLayout>
      <Outlet />
    </SidebarLayout>
  );
}
